import { BaseModel } from './base-model';
import { Project } from './project';

/** One `feature_source.fields` entry: a feature property name, or the property plus a value map. */
export interface FeatureSourceFieldMapping {
  field: string;
  /** Feature value -> form value; a value missing from the map is used as is. */
  values?: Record<string, string>;
}

/**
 * `control_form.ui_config.feature_source`: prefill header fields from the map feature linked in the
 * header Location picker. Pure data, evaluated the same way on web and mobile
 * (see `resolveFeatureSourcePrefill` in shared/helpers/feature-source-prefill.ts).
 */
export interface FeatureSource {
  /** project_reference_layer uuid (== manifest layer id == map_feature_ref.layerId). */
  layer: string;
  /** "only": the picker shows and picks from this layer alone. Omitted: the picker behaves as usual. */
  picker?: 'only';
  /** Equality filter on feature properties (string compare); only matching features are offered. */
  where?: Record<string, string | number | boolean>;
  /** Header field name -> feature property name, or `{ field, values }`. */
  fields?: Record<string, string | FeatureSourceFieldMapping>;
  /** Feature property -> separator, for properties holding several values (e.g. `{ "test_type": "," }`). */
  multi_value_fields?: MultiValueFields;
}

/** Feature property name -> separator (ArcGIS multiple-choice fields store "Density,Compaction"). */
export type MultiValueFields = Record<string, string>;

/** One `feature_launcher.routes` entry: features matching `when` open the form of control_form `type` `form_type`. */
export interface FeatureLauncherRoute {
  /** Same matcher as feature_source.where; empty or missing = default route. */
  when?: Record<string, string | number | boolean> | null;
  form_type: string;
}

/**
 * `control_form.ui_config.feature_launcher`: a map-first launcher form. Picking a feature of `layer` opens the
 * routed target form (by control_form `type` in the same project), linked and prefilled from the target form's
 * own feature_source. The launcher form itself is never saved. Target forms usually set `ui_config.launcher_only`.
 */
export interface FeatureLauncher {
  layer: string;
  /** Picker hint. */
  title?: string;
  /** Feature properties shown, joined " · ", on each chooser row. */
  label_fields?: string[];
  routes?: FeatureLauncherRoute[];
  /** As on feature_source: each value of these properties is offered as its own row (same point, same link). */
  multi_value_fields?: MultiValueFields;
}

/**
 * A form definition (control_form). Forms are project-scoped: an organization
 * shares a form by keeping a copy of it in each project that uses it, so the
 * org-wide listing also carries the project a form lives in and the other
 * projects that hold a copy of it.
 */
export class ControlForm extends BaseModel {

  // for CASL subject detection on code minification
  static get modelName() { return 'ControlForm'; }

  /* tslint:disable */
  name: string;
  description: string;
  type: string;
  ui_config: any;
  is_mandatory: boolean;
  daily_report_form: boolean;
  display_widget: string;
  display_format: string;
  fields: any[];

  project: Project;
  project_id: string;

  // org-wide listing only
  other_projects: { id: string, name: string }[];
  project_count: number;
  in_target_project: boolean;

  selected: boolean;
  /* tslint:enable */

  get relations() {
    return {
      project: Project,
    };
  }

  /**
   * Processes json obj and sets respective properties.
   * @param jsonObj json from api
   */
  parseJSONData(jsonObj: any) {
    super.parseJSONData(jsonObj);

    this.project_id = this.project?.id || this.project_id;
  }
}
